import React, { useState } from 'react';
import { PrimaryButton, Card, ScreenHeader } from '../ui';
import gamesService from '../../../services/gamesService';
import { useGameEligibility } from '../useGameEligibility';
import GameLockedCard from '../GameLockedCard';

interface DiceRollGameProps {
  appName: string;
  outletId: number;
  onClose?: () => void;
}

const PIP_LAYOUTS: Record<number, [number, number][]> = {
  1: [[50, 50]],
  2: [[25, 25], [75, 75]],
  3: [[25, 25], [50, 50], [75, 75]],
  4: [[25, 25], [75, 25], [25, 75], [75, 75]],
  5: [[25, 25], [75, 25], [50, 50], [25, 75], [75, 75]],
  6: [[25, 22], [75, 22], [25, 50], [75, 50], [25, 78], [75, 78]],
};

function DiceFace({ value }: { value: number }) {
  return (
    <div
      style={{
        width: 92,
        height: 92,
        background: '#FFFFFF',
        border: '1px solid var(--border-color, #E5E2D8)',
        borderRadius: 16,
        position: 'relative',
        boxShadow: '0 4px 16px rgba(0,0,0,.10)',
        margin: '20px auto',
      }}
    >
      {(PIP_LAYOUTS[value] ?? []).map(([x, y], i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: `${x}%`,
            top: `${y}%`,
            transform: 'translate(-50%, -50%)',
            width: 13,
            height: 13,
            borderRadius: '50%',
            background: 'var(--text-primary, #1F2428)',
          }}
        />
      ))}
    </div>
  );
}

const DiceRollGame: React.FC<DiceRollGameProps> = ({ appName, outletId, onClose }) => {
  const { loading, eligible, eligibility } = useGameEligibility(appName, 'DICE_ROLL');
  const [face, setFace] = useState(1);
  const [rolling, setRolling] = useState(false);
  const [played, setPlayed] = useState(false);
  const [resultLabel, setResultLabel] = useState<string | null>(null);
  const [redemptionCode, setRedemptionCode] = useState<string | null>(null);

  async function handleRoll() {
    if (rolling || played || !eligible) return;
    setRolling(true);
    setResultLabel(null);
    const spinInterval = setInterval(() => setFace(1 + Math.floor(Math.random() * 6)), 90);
    try {
      const res = await gamesService.spin(
        appName,
        'DICE_ROLL',
        gamesService.dailyIdempotencyKey(outletId, 'DICE_ROLL')
      );
      await new Promise((r) => setTimeout(r, 1200));
      clearInterval(spinInterval);
      const rolledValue = parseInt(res.resultSegment, 10);
      setFace(isNaN(rolledValue) ? 1 : rolledValue);
      const won = rolledValue === 6;
      setResultLabel(won ? 'You rolled a 6 - free add-on!' : `You rolled a ${rolledValue} - so close!`);
      setRedemptionCode(won ? res.redemptionCode : null);
      setPlayed(true);
    } catch {
      clearInterval(spinInterval);
      setResultLabel('Come back tomorrow for another roll.');
      setPlayed(true);
    }
    setRolling(false);
  }

  const locked = !loading && !eligible;

  return (
    <div style={{ background: 'var(--bg, #FFFFFF)', minHeight: '100vh', paddingBottom: 24 }}>
      <ScreenHeader
        title={<>Roll the <span style={{ color: '#D9472B' }}>Dice</span></>}
        subtitle="Roll a 6 and get a free add-on"
        onClose={onClose}
      />
      <div style={{ padding: '18px 24px 0' }}>
        {loading && (
          <p style={{ textAlign: 'center', color: 'var(--text-muted, #9B8F6E)', marginTop: 40 }}>Loading…</p>
        )}

        {locked && <GameLockedCard eligibility={eligibility} />}

        {!loading && eligible && (
          <>
            <DiceFace value={face} />

            {resultLabel && !rolling && (
              <>
                <p style={{ textAlign: 'center', fontSize: 13.5, color: 'var(--text-secondary, #6B5E3E)' }}>{resultLabel}</p>
                {redemptionCode && (
                  <Card style={{ textAlign: 'center', marginTop: 10 }}>
                    <div style={{ fontSize: 10.5, color: 'var(--text-muted, #9B8F6E)' }}>CODE</div>
                    <div style={{ fontWeight: 700, fontSize: 17, letterSpacing: 2 }}>{redemptionCode}</div>
                  </Card>
                )}
                <div style={{ height: 14 }} />
              </>
            )}

            <PrimaryButton onClick={handleRoll} disabled={rolling || played}>
              {rolling ? 'ROLLING…' : played ? 'COME BACK TOMORROW' : 'ROLL THE DICE'}
            </PrimaryButton>
          </>
        )}
      </div>
    </div>
  );
};

export default DiceRollGame;
